"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Image from "@modules/common/components/cdn-image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import {
  SiteHeroSlide,
  SitePageBlock,
  normalizeStorefrontHref,
} from "@lib/data/site-manager"

type Props = {
  block: SitePageBlock
}

const FALLBACK_GRADIENT =
  "bg-[linear-gradient(135deg,#1a1510_0%,#2c2318_50%,#181512_100%)]"

const alignClass = (align?: string) => {
  switch (align) {
    case "center":
      return "items-center text-center mx-auto"
    case "right":
      return "items-end text-right ml-auto"
    default:
      return "items-start text-left"
  }
}

// Inline copy block reused across desktop/mobile slot
function HeroCopy({
  slide,
  compact,
}: {
  slide: SiteHeroSlide
  compact?: boolean
}) {
  return (
    <div className={`flex max-w-3xl flex-col ${alignClass(slide.align)}`}>
      {slide.eyebrow && (
        <p
          className={`font-medium uppercase text-[#3D5240] ${
            compact
              ? "text-[10px] tracking-[0.3em]"
              : "text-[11px] tracking-[0.36em]"
          }`}
        >
          {slide.eyebrow}
        </p>
      )}
      {slide.title && (
        // Promotional slide headline — NOT an <h1>. The slider renders every
        // slide (and both a desktop + a mobile slot), so an <h1> here produced
        // ~8 H1s per page. The page's single semantic H1 is the sr-only one in
        // HomePage.tsx. This stays visually a display heading via the classes.
        <p
          className={`alif-serif font-normal leading-[1.05] text-white ${
            compact ? "mt-3 text-3xl" : "mt-4 text-4xl md:text-6xl"
          }`}
        >
          {slide.title}
        </p>
      )}
      {slide.subtitle && (
        <p
          className={`max-w-xl leading-7 text-white/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.5)] ${
            compact ? "mt-3 text-sm" : "mt-4 text-base md:text-lg"
          }`}
        >
          {slide.subtitle}
        </p>
      )}
      {(slide.buttonText || slide.secondaryButtonText) && (
        <div
          className={`flex flex-wrap items-center gap-3 ${
            compact ? "mt-5" : "mt-8"
          }`}
        >
          {slide.buttonText && slide.buttonLink && (
            <LocalizedClientLink
              href={normalizeStorefrontHref(slide.buttonLink)}
              className={`inline-flex items-center justify-center bg-white font-medium uppercase tracking-[0.22em] text-[#1A1A1A] transition-colors hover:bg-[#F5F5F5] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-black ${
                compact ? "h-10 px-6 text-[11px]" : "h-12 px-8 text-[12px]"
              }`}
            >
              {slide.buttonText}
            </LocalizedClientLink>
          )}
          {slide.secondaryButtonText && slide.secondaryButtonLink && (
            <LocalizedClientLink
              href={normalizeStorefrontHref(slide.secondaryButtonLink)}
              className={`inline-flex items-center justify-center border border-white/50 font-medium uppercase tracking-[0.22em] text-white transition-colors hover:border-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-black ${
                compact ? "h-10 px-6 text-[11px]" : "h-12 px-8 text-[12px]"
              }`}
            >
              {slide.secondaryButtonText}
            </LocalizedClientLink>
          )}
        </div>
      )}
    </div>
  )
}

export default function HeroSlider({ block }: Props) {
  const slides: SiteHeroSlide[] = Array.isArray(block.slides)
    ? block.slides.filter((s) => s && (s.title || s.imageUrl))
    : []

  // Legacy `lookbook_hero` / `hero` block → treat as single slide
  if (slides.length === 0 && (block.title || block.imageUrl)) {
    slides.push({
      eyebrow: block.eyebrow,
      title: block.title,
      subtitle: block.subtitle,
      imageUrl: block.imageUrl,
      mobileImageUrl: block.mobileImageUrl,
      imageAlt: block.imageAlt,
      buttonText: block.buttonText,
      buttonLink: block.buttonLink,
      secondaryButtonText: block.secondaryButtonText,
      secondaryButtonLink: block.secondaryButtonLink,
      align: (block.align as SiteHeroSlide["align"]) || "left",
    })
  }

  const autoplayMs = Math.max(0, Number(block.autoplayMs || 0))
  const [active, setActive] = useState(0)
  // Outgoing slide. The cross-fade keeps it fully opaque UNDERNEATH the incoming
  // slide (which fades in on top) so the dark section background never shows
  // through at the mid-point — i.e. no "dip to dark" flicker.
  const [prev, setPrev] = useState(0)
  const activeRef = useRef(0)
  const [paused, setPaused] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const total = slides.length

  const advance = useCallback(
    (next: number) => {
      if (total <= 1) return
      const safe = ((next % total) + total) % total
      if (safe === activeRef.current) return
      setPrev(activeRef.current)
      activeRef.current = safe
      setActive(safe)
    },
    [total]
  )

  const goTo = useCallback((i: number) => advance(i), [advance])
  const goNext = useCallback(() => advance(activeRef.current + 1), [advance])
  const goPrev = useCallback(() => advance(activeRef.current - 1), [advance])

  // Autoplay. Re-running on `active` change restarts the timer after EVERY
  // advance (manual or auto), so a manual nav can't be immediately followed by
  // an auto-advance (the "double jump").
  useEffect(() => {
    if (autoplayMs === 0 || total <= 1 || paused) return

    if (typeof window !== "undefined") {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)")
      if (reduce.matches) return
    }

    const id = setInterval(() => advance(activeRef.current + 1), autoplayMs)
    return () => clearInterval(id)
  }, [autoplayMs, total, paused, active, advance])

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden)
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (total <= 1) return
    if (e.key === "ArrowRight") {
      e.preventDefault()
      goNext()
    } else if (e.key === "ArrowLeft") {
      e.preventDefault()
      goPrev()
    } else if (e.key === " " && autoplayMs > 0) {
      e.preventDefault()
      setPaused((p) => !p)
    }
  }

  if (total === 0) return null

  // Two-layer cross-fade: the active slide sits on top and fades in; the
  // previous slide stays fully opaque directly beneath it (so there's never a
  // gap to the dark background); all others are hidden.
  const layerClass = (index: number) => {
    if (index === active) return "opacity-100 z-20"
    if (index === prev && prev !== active)
      return "opacity-100 z-10 pointer-events-none"
    return "opacity-0 z-0 pointer-events-none"
  }

  return (
    <section
      ref={wrapperRef}
      className="relative overflow-hidden bg-[#181512]"
      aria-roledescription="carousel"
      aria-label="Featured collections"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      tabIndex={total > 1 ? 0 : -1}
    >
      <div className="relative mx-auto w-full max-w-[2892px]">
        {/* Desktop slot — native 2892×1100 banner aspect (≈ 2.63:1) */}
        <div className="relative hidden w-full md:block md:aspect-[2892/1100]">
          {slides.map((slide, index) => {
            const isActive = index === active
            const alt = slide.imageAlt || slide.title || "Store hero"
            return (
              <div
                key={`hero-slide-desktop-${index}`}
                className={`absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none ${layerClass(
                  index
                )}`}
                aria-hidden={!isActive}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${total}`}
              >
                {slide.imageUrl ? (
                  <Image
                    src={slide.imageUrl}
                    alt={alt}
                    fill
                    optimize
                    priority={index === 0}
                    // sizes="0px" on mobile (md breakpoint = 768px) prevents the
                    // browser from downloading or preloading this slot's image on
                    // mobile where the slot is display:none — so we can safely
                    // use priority here without double-downloading the banner.
                    sizes="(min-width: 768px) 100vw, 0px"
                    quality={80}
                    className="object-cover object-center"
                  />
                ) : (
                  <div className={`absolute inset-0 ${FALLBACK_GRADIENT}`} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="content-container relative z-10 flex h-full flex-col justify-end pb-12 lg:pb-16">
                  <HeroCopy slide={slide} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Mobile slot — 4:5 portrait. If admin uploads a portrait crop via
            mobileImageUrl it'll fit. Otherwise the desktop image is shown
            cropped (object-center keeps the subject visible). */}
        <div className="relative block aspect-[4/5] w-full md:hidden">
          {slides.map((slide, index) => {
            const isActive = index === active
            const mobileSrc = slide.mobileImageUrl || slide.imageUrl
            const alt = slide.imageAlt || slide.title || "Store hero"
            return (
              <div
                key={`hero-slide-mobile-${index}`}
                className={`absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none ${layerClass(
                  index
                )}`}
                aria-hidden={!isActive}
              >
                {mobileSrc ? (
                  <Image
                    src={mobileSrc}
                    alt={alt}
                    fill
                    optimize
                    priority={index === 0}
                    // Mirror of the desktop guard: 0px on desktop so this slot's
                    // image isn't downloaded on desktop where the slot is hidden.
                    sizes="(max-width: 767px) 100vw, 0px"
                    quality={76}
                    className="object-cover object-center"
                  />
                ) : (
                  <div className={`absolute inset-0 ${FALLBACK_GRADIENT}`} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 px-6 pb-12">
                  <HeroCopy slide={slide} compact />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-white/35 bg-black/20 p-3 text-white backdrop-blur-sm transition-colors hover:bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:flex"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-white/35 bg-black/20 p-3 text-white backdrop-blur-sm transition-colors hover:bg-black/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:flex"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>

          <div
            className="absolute inset-x-0 bottom-5 z-20 flex justify-center gap-2 md:bottom-6"
            role="tablist"
            aria-label="Hero slides"
          >
            {slides.map((_, index) => (
              <button
                key={`hero-dot-${index}`}
                type="button"
                role="tab"
                aria-selected={index === active}
                aria-current={index === active}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => goTo(index)}
                className={`h-[3px] w-8 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                  index === active
                    ? "bg-white"
                    : "bg-white/35 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
